import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import client from "@/lib/api";
import { FeedPathTag } from "@/components/FeedPathTag";
import { VersionTag } from "@/components/VersionTag";
import { SyncFieldBadge } from "@/components/SyncFieldBadge";
import { Pencil, Save, Tag } from "lucide-react";

const CERT_FIELDS = [
  ["partner_plus_one", "1+ Partner", "Dealer.ExtraProperty[18].Active"],
  ["welt_auto", "Welt Auto", "Dealer.ExtraProperty[19].Active"],
  ["skoda_plus", "Skoda Plus", "Dealer.ExtraProperty[20].Active"],
  ["certified_seller", "Certified Seller", "Dealer.GeprVerk"],
  ["financial_certified", "Financial Certified", "Dealer.FinanzZert"],
  ["skoda_club", "Skoda Club", "Dealer.SkodaClub"],
];

/** Certifications (ExtraProperty) + SalesCentrum grouping — both small,
 * feed-synced dealer attributes, combined in one card. Saved together via
 * two PATCH calls so either can be edited from the same "Edit" toggle. */
export function DealerAttributesCard({ dealer, dealerId, onSaved, syncedFields = [], canManage = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const ddmMigrated = dealer.management_migrated_to_ddm === true;

  const start = () => {
    setDraft({
      certifications: { ...(dealer.certifications || {}) },
      sales_centrum: { ...(dealer.sales_centrum || {}) },
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const [, salesCentrumResp] = await Promise.all([
        client.patch(`/dealers/${dealerId}/certifications`, draft.certifications),
        client.patch(`/dealers/${dealerId}/sales-centrum`, draft.sales_centrum),
      ]);
      onSaved(salesCentrumResp.data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cert = (k) => (editing ? !!draft?.certifications?.[k] : !!dealer.certifications?.[k]);
  const centrum = (k) => (editing ? draft?.sales_centrum?.[k] ?? "" : dealer.sales_centrum?.[k] || "");

  return (
    <Card className="mb-6 p-6" data-testid="dealer-attributes-card">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">Certifications &amp; Sales Centrum <VersionTag version={dealer.card_versions?.certifications_sales_centrum} testId="version-tag-certifications_sales_centrum" /></h2>
        </div>
        {canManage && (
          !editing ? (
            <Button data-testid="edit-attributes-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button data-testid="save-attributes-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
                <Save className="h-3.5 w-3.5" />
                Save
              </Button>
            </div>
          )
        )}
      </div>

      <div className="mb-6">
        <p className="data-label mb-3">Certifications</p>
        <div className="flex flex-wrap gap-4">
          {CERT_FIELDS.map(([key, label, feedPath]) => (
            <label key={key} className="flex items-center gap-2 text-sm" data-testid={`cert-${key}-label`}>
              <Checkbox
                data-testid={`cert-${key}`}
                checked={cert(key)}
                disabled={!editing || saving}
                onCheckedChange={(v) =>
                  setDraft({ ...draft, certifications: { ...draft.certifications, [key]: !!v } })
                }
              />
              {label}
              <FeedPathTag path={feedPath} />
              <SyncFieldBadge path={`certifications.${key}`} editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <p className="data-label mb-3">Sales Centrum</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label className="data-label mb-1 flex items-center gap-1">
              VZ Number
              <FeedPathTag path="Dealer.SalesCentrum.VZNR" />
              <SyncFieldBadge path="sales_centrum.vz_number" editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
            </Label>
            <Input
              data-testid="sales-centrum-vz-number"
              disabled={!editing || saving}
              value={centrum("vz_number")}
              onChange={(e) =>
                setDraft({ ...draft, sales_centrum: { ...draft.sales_centrum, vz_number: e.target.value } })
              }
            />
          </div>
          <div>
            <Label className="data-label mb-1 flex items-center gap-1">
              VZ Name
              <FeedPathTag path="Dealer.SalesCentrum.VZNAME" />
              <SyncFieldBadge path="sales_centrum.vz_name" editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
            </Label>
            <Input
              data-testid="sales-centrum-vz-name"
              disabled={!editing || saving}
              value={centrum("vz_name")}
              onChange={(e) =>
                setDraft({ ...draft, sales_centrum: { ...draft.sales_centrum, vz_name: e.target.value } })
              }
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
